// Database module - SQLite via sql.js with File System Access API persistence

// Import sql.js - we'll load it from the global scope
// The sql-wasm.js file will expose initSqlJs globally
let SQL;
let db;
let dirHandle = null;
let fileHandle = null;

const DB_FILENAME = "gymnotes.db";
const IDB_NAME = "gymnotes-storage";
const IDB_STORE = "db-files";

// Load sql.js library dynamically
async function loadSqlJs() {
  if (window.initSqlJs) {
    return window.initSqlJs;
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "/js/lib/sql-wasm.js";
    script.onload = () => {
      if (window.initSqlJs) {
        resolve(window.initSqlJs);
      } else {
        reject(new Error("initSqlJs not found after loading script"));
      }
    };
    script.onerror = () => reject(new Error("Failed to load sql-wasm.js"));
    document.head.appendChild(script);
  });
}

// Initialize SQL.js and database
export async function initDatabase() {
  try {
    // Load SQL.js
    const initSqlJs = await loadSqlJs();
    SQL = await initSqlJs({
      locateFile: (file) => `/js/lib/${file}`,
    });

    // Try to load existing database
    const existingDb = await loadDatabase();

    if (existingDb) {
      db = new SQL.Database(existingDb);
      console.log("Loaded existing database");
      // Run migrations for existing databases
      await migrateToV2();
      await migrateToV3();
      await migrateToV4();
      await migrateToV5();
    } else {
      db = new SQL.Database();
      await createSchema();
      await seedData();
      // Set version for new databases
      db.run(`PRAGMA user_version = 5`);
      await saveDatabase();
      console.log("Created new database with schema and seed data");
    }

    return db;
  } catch (error) {
    console.error("Failed to initialize database:", error);
    throw error;
  }
}

// Create database schema
async function createSchema() {
  const schema = `
    -- User Settings
    CREATE TABLE IF NOT EXISTS UserSettings (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      name TEXT,
      birthdate DATE,
      height INTEGER,
      created_at DATETIME DEFAULT CURRENT_TIMESTAMP
    );

    -- Weight History (for tracking weight over time)
    CREATE TABLE IF NOT EXISTS WeightHistory (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      user_settings_id INTEGER NOT NULL,
      weight DECIMAL NOT NULL,
      recorded_at DATETIME DEFAULT CURRENT_TIMESTAMP,
      FOREIGN KEY (user_settings_id) REFERENCES UserSettings(id) ON DELETE CASCADE
    );

    -- Exercises
    CREATE TABLE IF NOT EXISTS Exercises (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      name TEXT NOT NULL,
      musclegroup TEXT NOT NULL,
      equipment TEXT,
      color TEXT DEFAULT '#808080',
      exercise_type TEXT DEFAULT 'weight_reps' CHECK(exercise_type IN ('weight_reps', 'reps_only', 'time_based', 'cardio_jogging', 'cardio_intervals'))
    );

    -- Workout Templates
    CREATE TABLE IF NOT EXISTS Template (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      name TEXT NOT NULL,
      notes TEXT CHECK(length(notes) <= 500)
    );

    -- Planned Exercises (junction table)
    CREATE TABLE IF NOT EXISTS PlannedExercises (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      workout_id INTEGER NOT NULL,
      exercise_id INTEGER NOT NULL,
      notes TEXT CHECK(length(notes) <= 500),
      FOREIGN KEY (workout_id) REFERENCES Template(id) ON DELETE CASCADE,
      FOREIGN KEY (exercise_id) REFERENCES Exercises(id) ON DELETE CASCADE
    );

    -- Planned Sets
    CREATE TABLE IF NOT EXISTS PlannedSets (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      weight DECIMAL,
      reps INTEGER,
      duration_seconds INTEGER,
      workout_exercise_id INTEGER NOT NULL,
      FOREIGN KEY (workout_exercise_id) REFERENCES PlannedExercises(id) ON DELETE CASCADE
    );

    -- Performed Workouts (completed workouts)
    CREATE TABLE IF NOT EXISTS PerformedWorkout (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      template_id INTEGER,
      workout_type TEXT DEFAULT 'strength' CHECK(workout_type IN ('strength', 'cardio')),
      date DATE DEFAULT (date('now')),
      start_time TIME,
      end_time TIME,
      studio TEXT,
      notes TEXT CHECK(length(notes) <= 500),
      FOREIGN KEY (template_id) REFERENCES Template(id)
    );

    -- Performed Exercises (exercises in a performed workout instance)
    CREATE TABLE IF NOT EXISTS PerformedExercises (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      workout_instance_id INTEGER NOT NULL,
      exercise_id INTEGER NOT NULL,
      workout_exercise_id INTEGER,
      FOREIGN KEY (workout_instance_id) REFERENCES PerformedWorkout(id) ON DELETE CASCADE,
      FOREIGN KEY (exercise_id) REFERENCES Exercises(id),
      FOREIGN KEY (workout_exercise_id) REFERENCES PlannedExercises(id) ON DELETE SET NULL
    );

    -- Performed Sets (actual sets in completed workouts)
    CREATE TABLE IF NOT EXISTS PerformedSets (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      workout_instance_id INTEGER NOT NULL,
      exercise_id INTEGER NOT NULL,
      planned_set_id INTEGER,
      performed_exercise_id INTEGER,
      set_number INTEGER,
      weight DECIMAL,
      reps INTEGER,
      duration_seconds INTEGER,
      rest_seconds INTEGER,
      notes TEXT CHECK(length(notes) <= 500),
      FOREIGN KEY (workout_instance_id) REFERENCES PerformedWorkout(id) ON DELETE CASCADE,
      FOREIGN KEY (exercise_id) REFERENCES Exercises(id),
      FOREIGN KEY (planned_set_id) REFERENCES PlannedSets(id) ON DELETE SET NULL,
      FOREIGN KEY (performed_exercise_id) REFERENCES PerformedExercises(id) ON DELETE CASCADE
    );

    -- Cardio Data (cardio-specific metrics)
    CREATE TABLE IF NOT EXISTS CardioData (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      performed_set_id INTEGER NOT NULL UNIQUE,
      distance_km DECIMAL,
      duration_minutes INTEGER,
      pace_min_per_km DECIMAL,
      speed_km_per_h DECIMAL,
      sprint_speed_kmh DECIMAL,
      sprint_duration_sec INTEGER,
      rest_speed_kmh DECIMAL,
      rest_duration_sec INTEGER,
      repetitions INTEGER,
      total_distance_km DECIMAL,
      total_duration_minutes INTEGER,
      FOREIGN KEY (performed_set_id) REFERENCES PerformedSets(id) ON DELETE CASCADE
    );

    -- Indexes for performance
    CREATE INDEX IF NOT EXISTS idx_workout_exercises_workout ON PlannedExercises(workout_id);
    CREATE INDEX IF NOT EXISTS idx_workout_exercises_exercise ON PlannedExercises(exercise_id);
    CREATE INDEX IF NOT EXISTS idx_planned_sets_workout_exercise ON PlannedSets(workout_exercise_id);
    CREATE INDEX IF NOT EXISTS idx_performed_exercises_instance ON PerformedExercises(workout_instance_id);
    CREATE INDEX IF NOT EXISTS idx_performed_exercises_exercise ON PerformedExercises(exercise_id);
    CREATE INDEX IF NOT EXISTS idx_performed_sets_workout ON PerformedSets(workout_instance_id);
    CREATE INDEX IF NOT EXISTS idx_performed_sets_exercise ON PerformedSets(exercise_id);
    CREATE INDEX IF NOT EXISTS idx_performed_sets_planned_set ON PerformedSets(planned_set_id);
    CREATE INDEX IF NOT EXISTS idx_performed_sets_performed_exercise ON PerformedSets(performed_exercise_id);
    CREATE INDEX IF NOT EXISTS idx_workout_instance_date ON PerformedWorkout(date);
    CREATE INDEX IF NOT EXISTS idx_weight_history_user ON WeightHistory(user_settings_id);
    CREATE INDEX IF NOT EXISTS idx_weight_history_date ON WeightHistory(recorded_at);
    CREATE INDEX IF NOT EXISTS idx_cardio_data_performed_set ON CardioData(performed_set_id);
  `;

  db.run(schema);
}

// Seed initial data
async function seedData() {
  // Create default user settings
  db.run(
    `INSERT INTO UserSettings (name) VALUES ('User')`
  );

  // Seed exercises with finalized list
  const exercises = [
    // Chest
    ["Bench Press", "chest", "Barbell", "#FF6B6B", "weight_reps"],
    ["Chest Press", "chest", "Machine", "#FF6B6B", "weight_reps"],
    ["Incline Dumbbell Press", "chest", "Dumbbell", "#FF6B6B", "weight_reps"],
    ["Incline Bench Press", "chest", "Barbell", "#FF6B6B", "weight_reps"],
    ["Chest Fly", "chest", "Machine", "#FF6B6B", "weight_reps"],

    // Back
    ["Lat Pulldown", "back", "Cable Machine", "#4ECDC4", "weight_reps"],
    ["Pull-Ups", "back", "Pull-up Machine", "#4ECDC4", "reps_only"],
    ["Barbell Row", "back", "Barbell", "#4ECDC4", "weight_reps"],
    ["Rear Delt Row", "back", "Machine", "#4ECDC4", "weight_reps"],
    ["Back Extension", "back", "Machine", "#4ECDC4", "weight_reps"],

    // Shoulders
    ["Shoulder Press", "shoulders", "Machine", "#95E1D3", "weight_reps"],
    ["Lateral Raises", "shoulders", "Cable Machine", "#95E1D3", "weight_reps"],
    ["Rear Fly", "shoulders", "Machine", "#95E1D3", "weight_reps"],

    // Legs
    ["Squats", "legs", "Barbell", "#F38181", "weight_reps"],
    ["Leg Press", "legs", "Machine", "#F38181", "weight_reps"],
    ["Leg Extension", "legs", "Machine", "#F38181", "weight_reps"],
    ["Leg Curl", "legs", "Machine", "#F38181", "weight_reps"],
    ["Deadlift", "legs", "Barbell", "#F38181", "weight_reps"],
    ["Hip Adduction", "legs", "Machine", "#F38181", "weight_reps"],
    ["Hip Abduction", "legs", "Machine", "#F38181", "weight_reps"],
    ["Barbell Squat", "legs", "Barbell", "#F38181", "weight_reps"],

    // Triceps
    ["Cable Triceps Pushdown", "triceps", "Cable Machine", "#AA96DA", "weight_reps"],
    ["Seated Triceps Press Machine", "triceps", "Machine", "#AA96DA", "weight_reps"],

    // Biceps
    ["Barbell Curl", "biceps", "Barbell", "#FCBAD3", "weight_reps"],

    // Core
    ["Rotary Torso", "core", "Machine", "#FFFFD2", "weight_reps"],
    ["Crunches", "core", "Bodyweight", "#FFFFD2", "reps_only"],
    ["Ab Crunch Machine", "core", "Machine", "#FFFFD2", "weight_reps"],
    ["Plank", "core", "Bodyweight", "#FFFFD2", "time_based"],

    // Cardio
    ["Jogging", "cardio", "Treadmill", "#FF9500", "cardio_jogging"],
    ["Outdoor Running", "cardio", "Outdoor", "#FF9500", "cardio_jogging"],
    ["Sprint Intervals", "cardio", "Treadmill", "#FF9500", "cardio_intervals"],
  ];

  const stmt = db.prepare(
    "INSERT INTO Exercises (name, musclegroup, equipment, color, exercise_type) VALUES (?, ?, ?, ?, ?)"
  );
  exercises.forEach((ex) => stmt.run(ex));
  stmt.free();
}

// Migrate database to version 2 (add exercise_type and duration_seconds)
async function migrateToV2() {
  try {
    const result = db.exec('PRAGMA user_version');
    const currentVersion = result[0]?.values[0]?.[0] || 0;

    if (currentVersion < 2) {
      console.log('Migrating database to version 2...');

      // Add exercise_type column to Exercises table
      db.run(`ALTER TABLE Exercises ADD COLUMN exercise_type TEXT DEFAULT 'weight_reps'`);

      // Add duration_seconds to PlannedSets
      db.run(`ALTER TABLE PlannedSets ADD COLUMN duration_seconds INTEGER`);

      // Add duration_seconds to PerformedSets
      db.run(`ALTER TABLE PerformedSets ADD COLUMN duration_seconds INTEGER`);

      // Update existing exercises to set their types
      db.run(`UPDATE Exercises SET exercise_type = 'reps_only' WHERE name = 'Crunches'`);
      db.run(`UPDATE Exercises SET exercise_type = 'time_based' WHERE name = 'Plank'`);
      db.run(`UPDATE Exercises SET exercise_type = 'reps_only' WHERE name = 'Pull-Ups'`);

      // Set database version to 2
      db.run(`PRAGMA user_version = 2`);

      await saveDatabase();
      console.log('Database migration to version 2 complete');
    }
  } catch (error) {
    console.error('Migration error:', error);
    throw error;
  }
}

// Migrate database to version 3 (add cardio support)
async function migrateToV3() {
  try {
    const result = db.exec('PRAGMA user_version');
    const currentVersion = result[0]?.values[0]?.[0] || 0;

    if (currentVersion < 3) {
      console.log('Migrating database to version 3...');

      // 1. Recreate PerformedWorkout table with nullable template_id and workout_type
      db.run(`
        CREATE TABLE PerformedWorkout_new (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          template_id INTEGER,
          workout_type TEXT DEFAULT 'strength' CHECK(workout_type IN ('strength', 'cardio')),
          date DATE DEFAULT (date('now')),
          start_time TIME,
          end_time TIME,
          studio TEXT,
          notes TEXT CHECK(length(notes) <= 500),
          FOREIGN KEY (template_id) REFERENCES Template(id)
        );
      `);

      // Copy existing data (all existing workouts are strength workouts)
      db.run(`
        INSERT INTO PerformedWorkout_new (id, template_id, date, start_time, end_time, studio, notes)
        SELECT id, template_id, date, start_time, end_time, studio, notes
        FROM PerformedWorkout;
      `);

      // Drop old table and rename new one
      db.run(`DROP TABLE PerformedWorkout;`);
      db.run(`ALTER TABLE PerformedWorkout_new RENAME TO PerformedWorkout;`);

      // Recreate index
      db.run(`CREATE INDEX IF NOT EXISTS idx_workout_instance_date ON PerformedWorkout(date);`);

      // 2. Create CardioData table
      db.run(`
        CREATE TABLE IF NOT EXISTS CardioData (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          performed_set_id INTEGER NOT NULL UNIQUE,
          distance_km DECIMAL,
          duration_minutes INTEGER,
          pace_min_per_km DECIMAL,
          speed_km_per_h DECIMAL,
          sprint_speed_kmh DECIMAL,
          sprint_duration_sec INTEGER,
          rest_speed_kmh DECIMAL,
          rest_duration_sec INTEGER,
          repetitions INTEGER,
          total_distance_km DECIMAL,
          total_duration_minutes INTEGER,
          FOREIGN KEY (performed_set_id) REFERENCES PerformedSets(id) ON DELETE CASCADE
        );
      `);

      db.run(`CREATE INDEX IF NOT EXISTS idx_cardio_data_performed_set ON CardioData(performed_set_id);`);

      // 3. Seed cardio exercises
      const cardioExercises = [
        ['Jogging', 'cardio', 'Treadmill', '#FF9500', 'cardio_jogging'],
        ['Outdoor Running', 'cardio', 'Outdoor', '#FF9500', 'cardio_jogging'],
        ['Sprint Intervals', 'cardio', 'Treadmill', '#FF9500', 'cardio_intervals'],
      ];

      const stmt = db.prepare(
        'INSERT INTO Exercises (name, musclegroup, equipment, color, exercise_type) VALUES (?, ?, ?, ?, ?)'
      );
      cardioExercises.forEach(ex => stmt.run(ex));
      stmt.free();

      // Set database version to 3
      db.run(`PRAGMA user_version = 3`);

      await saveDatabase();
      console.log('Database migration to version 3 complete');
    }
  } catch (error) {
    console.error('Migration error:', error);
    throw error;
  }
}

// Migrate database to version 4 (remove rest timer feature)
async function migrateToV4() {
  try {
    const result = db.exec('PRAGMA user_version');
    const currentVersion = result[0]?.values[0]?.[0] || 0;

    if (currentVersion < 4) {
      console.log('Migrating database to version 4...');

      // SQLite doesn't support DROP COLUMN directly in older versions
      // We need to recreate the table
      db.run(`
        CREATE TABLE UserSettings_new (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          name TEXT,
          birthdate DATE,
          height INTEGER,
          created_at DATETIME DEFAULT CURRENT_TIMESTAMP
        );
      `);

      // Copy existing data (excluding default_rest_seconds)
      db.run(`
        INSERT INTO UserSettings_new (id, name, birthdate, height, created_at)
        SELECT id, name, birthdate, height, created_at
        FROM UserSettings;
      `);

      // Drop old table and rename new one
      db.run(`DROP TABLE UserSettings;`);
      db.run(`ALTER TABLE UserSettings_new RENAME TO UserSettings;`);

      // Recreate index if any (there are none for UserSettings currently)

      // Set database version to 4
      db.run(`PRAGMA user_version = 4`);

      await saveDatabase();
      console.log('Database migration to version 4 complete');
    }
  } catch (error) {
    console.error('Migration error:', error);
    throw error;
  }
}

// Migrate database to version 5 (add PerformedExercises table and update PerformedSets)
async function migrateToV5() {
  try {
    const result = db.exec('PRAGMA user_version');
    const currentVersion = result[0]?.values[0]?.[0] || 0;

    if (currentVersion < 5) {
      console.log('Migrating database to version 5...');

      // Create PerformedExercises table
      db.run(`
        CREATE TABLE IF NOT EXISTS PerformedExercises (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          workout_instance_id INTEGER NOT NULL,
          exercise_id INTEGER NOT NULL,
          workout_exercise_id INTEGER,
          FOREIGN KEY (workout_instance_id) REFERENCES PerformedWorkout(id) ON DELETE CASCADE,
          FOREIGN KEY (exercise_id) REFERENCES Exercises(id),
          FOREIGN KEY (workout_exercise_id) REFERENCES PlannedExercises(id) ON DELETE SET NULL
        );
      `);

      // Recreate PerformedSets table with new columns
      db.run(`
        CREATE TABLE PerformedSets_new (
          id INTEGER PRIMARY KEY AUTOINCREMENT,
          workout_instance_id INTEGER NOT NULL,
          exercise_id INTEGER NOT NULL,
          planned_set_id INTEGER,
          performed_exercise_id INTEGER,
          set_number INTEGER,
          weight DECIMAL,
          reps INTEGER,
          duration_seconds INTEGER,
          rest_seconds INTEGER,
          notes TEXT CHECK(length(notes) <= 500),
          FOREIGN KEY (workout_instance_id) REFERENCES PerformedWorkout(id) ON DELETE CASCADE,
          FOREIGN KEY (exercise_id) REFERENCES Exercises(id),
          FOREIGN KEY (planned_set_id) REFERENCES PlannedSets(id) ON DELETE SET NULL,
          FOREIGN KEY (performed_exercise_id) REFERENCES PerformedExercises(id) ON DELETE CASCADE
        );
      `);

      // Copy existing PerformedSets data (planned_set_id and performed_exercise_id will be NULL)
      db.run(`
        INSERT INTO PerformedSets_new (id, workout_instance_id, exercise_id, set_number, weight, reps, duration_seconds, rest_seconds, notes)
        SELECT id, workout_instance_id, exercise_id, set_number, weight, reps, duration_seconds, rest_seconds, notes
        FROM PerformedSets;
      `);

      // Drop old table and rename new one
      db.run(`DROP TABLE PerformedSets;`);
      db.run(`ALTER TABLE PerformedSets_new RENAME TO PerformedSets;`);

      // Create indexes
      db.run(`CREATE INDEX IF NOT EXISTS idx_performed_exercises_instance ON PerformedExercises(workout_instance_id);`);
      db.run(`CREATE INDEX IF NOT EXISTS idx_performed_exercises_exercise ON PerformedExercises(exercise_id);`);
      db.run(`CREATE INDEX IF NOT EXISTS idx_performed_sets_workout ON PerformedSets(workout_instance_id);`);
      db.run(`CREATE INDEX IF NOT EXISTS idx_performed_sets_exercise ON PerformedSets(exercise_id);`);
      db.run(`CREATE INDEX IF NOT EXISTS idx_performed_sets_planned_set ON PerformedSets(planned_set_id);`);
      db.run(`CREATE INDEX IF NOT EXISTS idx_performed_sets_performed_exercise ON PerformedSets(performed_exercise_id);`);

      // Set database version to 5
      db.run(`PRAGMA user_version = 5`);

      await saveDatabase();
      console.log('Database migration to version 5 complete');
    }
  } catch (error) {
    console.error('Migration error:', error);
    throw error;
  }
}

// Check if a database exists (without creating one)
export async function databaseExists() {
  const existingDb = await loadDatabase();
  return existingDb !== null && existingDb !== undefined;
}

// Load database from File System Access API or IndexedDB
async function loadDatabase() {
  // Try File System Access API first
  if ("showOpenFilePicker" in window) {
    const stored = await loadFromIndexedDB("dirHandle");
    if (stored) {
      try {
        dirHandle = stored;
        const permission = await dirHandle.queryPermission({
          mode: "readwrite",
        });
        if (permission === "granted" || permission === "prompt") {
          fileHandle = await dirHandle.getFileHandle(DB_FILENAME);
          const file = await fileHandle.getFile();
          const buffer = await file.arrayBuffer();
          return new Uint8Array(buffer);
        }
      } catch (err) {
        console.log("Could not load from File System Access API:", err.message);
      }
    }
  }

  // Fall back to IndexedDB
  const dbData = await loadFromIndexedDB("db-data");
  return dbData;
}

// Save database to File System Access API or IndexedDB
export async function saveDatabase() {
  if (!db) return;

  const data = db.export();

  // Try File System Access API first
  if ("showOpenFilePicker" in window && dirHandle) {
    try {
      if (!fileHandle) {
        fileHandle = await dirHandle.getFileHandle(DB_FILENAME, {
          create: true,
        });
      }
      const writable = await fileHandle.createWritable();
      await writable.write(data);
      await writable.close();
      console.log("Database saved via File System Access API");
      return;
    } catch (err) {
      console.log("Could not save via File System Access API:", err.message);
    }
  }

  // Fall back to IndexedDB
  await saveToIndexedDB("db-data", data);
  console.log("Database saved via IndexedDB");
}

// Request directory access for File System Access API
export async function requestDirectoryAccess() {
  if (!("showDirectoryPicker" in window)) {
    throw new Error("File System Access API not supported");
  }

  try {
    dirHandle = await window.showDirectoryPicker({
      mode: "readwrite",
    });

    // Save directory handle for future use
    await saveToIndexedDB("dirHandle", dirHandle);

    // Try to save database immediately
    await saveDatabase();

    return true;
  } catch (err) {
    console.error("Directory access denied:", err);
    return false;
  }
}

// IndexedDB helpers
function openIndexedDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(IDB_NAME, 1);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
  });
}

async function saveToIndexedDB(key, value) {
  const idb = await openIndexedDB();
  return new Promise((resolve, reject) => {
    const tx = idb.transaction(IDB_STORE, "readwrite");
    const store = tx.objectStore(IDB_STORE);
    const request = store.put(value, key);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

async function loadFromIndexedDB(key) {
  const idb = await openIndexedDB();
  return new Promise((resolve, reject) => {
    const tx = idb.transaction(IDB_STORE, "readonly");
    const store = tx.objectStore(IDB_STORE);
    const request = store.get(key);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Query helpers
export function query(sql, params = []) {
  if (!db) throw new Error("Database not initialized");

  try {
    const stmt = db.prepare(sql);
    stmt.bind(params);

    const results = [];
    while (stmt.step()) {
      results.push(stmt.getAsObject());
    }
    stmt.free();

    return results;
  } catch (err) {
    console.error("Query error:", err, sql, params);
    throw err;
  }
}

export function run(sql, params = []) {
  if (!db) throw new Error("Database not initialized");

  try {
    db.run(sql, params);
    return db.exec("SELECT last_insert_rowid() as id")[0]?.values[0]?.[0];
  } catch (err) {
    console.error("Run error:", err, sql, params);
    throw err;
  }
}

export function exec(sql) {
  if (!db) throw new Error("Database not initialized");

  try {
    return db.exec(sql);
  } catch (err) {
    console.error("Exec error:", err, sql);
    throw err;
  }
}

// Get database instance
export function getDatabase() {
  return db;
}

// Export database as file download
export async function exportDatabaseFile() {
  if (!db) throw new Error("Database not initialized");

  const data = db.export(); // Uint8Array
  const blob = new Blob([data], { type: "application/x-sqlite3" });
  const url = URL.createObjectURL(blob);

  // Trigger download
  const a = document.createElement("a");
  a.href = url;
  a.download = `gymnotes-backup-${new Date().toISOString().split("T")[0]}.db`;
  a.click();

  URL.revokeObjectURL(url);
}

// Export database as Uint8Array (for programmatic use)
export function exportDatabase() {
  if (!db) throw new Error("Database not initialized");
  return db.export();
}

// Import database from File object
export async function importDatabaseFile(file) {
  // Ensure SQL.js is loaded
  if (!SQL) {
    const initSqlJs = await loadSqlJs();
    SQL = await initSqlJs({
      locateFile: (file) => `/js/lib/${file}`,
    });
  }

  const arrayBuffer = await file.arrayBuffer();
  const data = new Uint8Array(arrayBuffer);

  // Validate it's a SQLite database
  if (!validateSQLiteFile(data)) {
    throw new Error("Invalid database file - not a valid SQLite database");
  }

  // Close current database
  if (db) db.close();

  // Load new database
  db = new SQL.Database(data);

  // Verify it has required tables
  const tables = db.exec("SELECT name FROM sqlite_master WHERE type='table'");
  const tableNames = tables[0]?.values?.map(row => row[0]) || [];

  const requiredTables = ['UserSettings', 'Exercises', 'Template', 'PerformedWorkout', 'PerformedSets'];
  const missingTables = requiredTables.filter(t => !tableNames.includes(t));

  if (missingTables.length > 0) {
    throw new Error(`Invalid database - missing tables: ${missingTables.join(', ')}`);
  }

  // Run migrations if needed
  await migrateToV2();
  await migrateToV3();

  // Save to storage
  await saveDatabase();

  return db;
}

// Import database from Uint8Array (for programmatic use)
export function importDatabase(data) {
  if (db) db.close();
  db = new SQL.Database(data);
  return db;
}

// Validate SQLite file format
function validateSQLiteFile(data) {
  // SQLite files start with "SQLite format 3\0"
  if (data.length < 16) return false;
  const header = String.fromCharCode.apply(null, data.slice(0, 16));
  return header.startsWith("SQLite format 3");
}

// Reset database to fresh state with seed data
export async function resetDatabase() {
  if (db) db.close();

  db = new SQL.Database();
  await createSchema();
  await seedData();
  db.run("PRAGMA user_version = 4");
  await saveDatabase();

  return db;
}
